/**
 * Поле ввода с премиальными эмодзи.
 *
 * ── Зачем не textarea ───────────────────────────────────────────────────────
 *
 * Премиальный эмодзи — это не символ, а картинка с идентификатором. В обычном
 * <textarea> его нельзя ни показать, ни отличить от запасного символа: там
 * живёт только текст. Поэтому здесь contentEditable: премиальные эмодзи —
 * инлайновые <img>, всё остальное — обычный текст.
 *
 * ── Контракт наружу ─────────────────────────────────────────────────────────
 *
 * Компонент отдаёт пару {text, entities}: text — с базовым символом на месте
 * каждого премиального эмодзи, entities — их смещения в UTF-16 (ровно то, что
 * ждёт Telegram и наш API). Родитель не знает про DOM: он получает значение и
 * умеет вставить эмодзи или текст через ref.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import type { TelegramTextEntityDto } from "@tw/shared-types";
import { cn } from "@/lib/utils";
import { parseClipboardHtml } from "@/lib/rich-clipboard.ts";

export interface RichValue {
  text: string;
  entities: TelegramTextEntityDto[];
}

export interface RichTextInputHandle {
  insertText: (text: string) => void;
  insertCustomEmoji: (customEmojiId: string, emoji: string, src: string) => void;
  /** Заполнить поле готовым значением (текст + премиальные эмодзи). */
  setValue: (value: RichValue) => void;
  clear: () => void;
  focus: () => void;
}

interface RichTextInputProps {
  value: RichValue;
  onChange: (value: RichValue) => void;
  onEnter?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

/** Собрать {text, entities} из содержимого contentEditable. */
function readContent(root: HTMLElement): RichValue {
  let text = "";
  const entities: TelegramTextEntityDto[] = [];

  const walk = (node: Node): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent ?? "";
      } else if (child instanceof HTMLImageElement && child.dataset.emojiId) {
        const emoji = child.dataset.emojiChar || "🙂";
        entities.push({
          type: "customEmoji",
          offset: text.length,
          length: emoji.length,
          customEmojiId: child.dataset.emojiId,
        });
        text += emoji;
      } else if (child instanceof HTMLBRElement) {
        text += "\n";
      } else if (child instanceof HTMLElement) {
        // <div>, вставленный браузером на Enter, — это перевод строки перед
        // содержимым, кроме самого первого блока.
        if (child.tagName === "DIV" && text.length > 0) text += "\n";
        walk(child);
      }
    }
  };
  walk(root);
  // contentEditable добавляет висячий перевод строки — не отдаём его наружу.
  return { text: text.replace(/\n$/, ""), entities };
}

/**
 * DOM из значения: текстовые куски — узлами, премиальные эмодзи — картинками.
 * Смещения в UTF-16, как их отдаёт API. Общий для заполнения поля и вставки
 * из буфера — иначе два разбора расходились бы.
 */
function fragmentOf(v: RichValue): DocumentFragment {
  const emojis = v.entities
    .filter((e) => e.type === "customEmoji" && e.customEmojiId)
    .sort((a, b) => a.offset - b.offset);
  const frag = document.createDocumentFragment();
  let cursor = 0;
  for (const e of emojis) {
    if (e.offset < cursor) continue;
    if (e.offset > cursor) {
      frag.appendChild(document.createTextNode(v.text.slice(cursor, e.offset)));
    }
    const char = v.text.slice(e.offset, e.offset + e.length);
    frag.appendChild(
      emojiImg(
        e.customEmojiId as string,
        char,
        `/api/custom-emoji/${encodeURIComponent(e.customEmojiId as string)}`,
      ),
    );
    cursor = e.offset + e.length;
  }
  if (cursor < v.text.length) {
    frag.appendChild(document.createTextNode(v.text.slice(cursor)));
  }
  return frag;
}

/** Элемент премиального эмодзи. */
function emojiImg(customEmojiId: string, emoji: string, src: string): HTMLImageElement {
  const img = document.createElement("img");
  img.src = src;
  img.alt = emoji;
  img.dataset.emojiId = customEmojiId;
  img.dataset.emojiChar = emoji;
  img.contentEditable = "false";
  img.className = "inline-block size-[1.3em] align-[-0.28em]";
  return img;
}

export const RichTextInput = forwardRef<RichTextInputHandle, RichTextInputProps>(
  function RichTextInput(
    { value, onChange, onEnter, placeholder, disabled, className, ariaLabel },
    ref,
  ) {
    const boxRef = useRef<HTMLDivElement>(null);
    // Гасим onChange, порождённый нашим же программным изменением DOM.
    const muted = useRef(false);
    // Последнее положение курсора внутри поля — чтобы вставка из пикера
    // попадала туда, где стоял курсор, даже если фокус на миг ушёл на кнопку.
    const savedRange = useRef<Range | null>(null);
    const rememberRange = (): void => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && boxRef.current?.contains(sel.anchorNode)) {
        savedRange.current = sel.getRangeAt(0).cloneRange();
      }
    };

    const emit = useCallback(() => {
      if (!boxRef.current) return;
      onChange(readContent(boxRef.current));
    }, [onChange]);

    /*
     * Внешнее обнуление (после отправки) — единственная синхронизация value →
     * DOM. Полноценно отражать value в DOM на каждый рендер нельзя: это сбило
     * бы курсор при каждом нажатии. Поэтому родитель управляет полем через
     * ref (insert*), а value читает; сюда приходит только «стало пусто».
     */
    useEffect(() => {
      const el = boxRef.current;
      if (el && value.text === "" && value.entities.length === 0 && el.textContent !== "") {
        el.replaceChildren();
      }
    }, [value]);

    /**
     * Поставить курсор туда, где он был.
     *
     * После клика по кнопке пикера выделение в поле теряется (особенно внутри
     * модалки Radix с ловушкой фокуса). Восстанавливаем последнее сохранённое
     * положение; если его нет — ставим в конец. Только после этого вставляем.
     */
    const restoreCaret = (): void => {
      const el = boxRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) return;
      const range = document.createRange();
      if (savedRange.current && el.contains(savedRange.current.startContainer)) {
        range.setStart(savedRange.current.startContainer, savedRange.current.startOffset);
        range.collapse(true);
      } else {
        range.selectNodeContents(el);
        range.collapse(false);
      }
      sel?.removeAllRanges();
      sel?.addRange(range);
    };

    const insertNode = (node: Node): void => {
      const el = boxRef.current;
      if (!el) return;
      restoreCaret();
      const sel = window.getSelection();
      const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
      // У фрагмента после вставки детей не остаётся — курсор ставим за его
      // последним узлом, запомненным заранее.
      const tail = node instanceof DocumentFragment ? node.lastChild : node;
      if (range && el.contains(range.startContainer) && tail) {
        range.deleteContents();
        range.insertNode(node);
        range.setStartAfter(tail);
        range.setEndAfter(tail);
        sel?.removeAllRanges();
        sel?.addRange(range);
        savedRange.current = range.cloneRange();
      } else {
        el.appendChild(node);
      }
      emit();
    };

    useImperativeHandle(ref, () => ({
      insertText: (t: string) => insertNode(document.createTextNode(t)),
      insertCustomEmoji: (id, emoji, src) => insertNode(emojiImg(id, emoji, src)),
      setValue: (v: RichValue) => {
        const el = boxRef.current;
        if (!el) return;
        el.replaceChildren(fragmentOf(v));
        onChange(v);
      },
      clear: () => {
        boxRef.current?.replaceChildren();
        emit();
      },
      focus: () => boxRef.current?.focus(),
    }));

    const isEmpty = value.text.length === 0 && value.entities.length === 0;

    return (
      <div className="relative min-w-0 flex-1">
        <div
          ref={boxRef}
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={() => {
            if (muted.current) return;
            rememberRange();
            emit();
          }}
          onKeyUp={rememberRange}
          onMouseUp={rememberRange}
          onBlur={rememberRange}
          onPaste={(e) => {
            /*
             * Из буфера берём текст и ТОЛЬКО премиальные эмодзи.
             *
             * Раньше вставлялся один text/plain, и скопированное из
             * переписки сообщение теряло премиальные эмодзи: в plain-части
             * на их месте запасной символ без идентификатора. Идентификатор
             * есть лишь в HTML — его и разбираем, а остальную чужую разметку
             * (жирный, ссылки, картинки) по-прежнему не пускаем: поле её не
             * понимает, и сериализация бы сломалась.
             */
            e.preventDefault();
            const rich = parseClipboardHtml(e.clipboardData.getData("text/html"));
            if (rich) {
              insertNode(fragmentOf(rich));
              return;
            }
            const text = e.clipboardData.getData("text/plain");
            insertNode(document.createTextNode(text));
          }}
          onKeyDown={(e) => {
            // Enter во время набора через IME (китайский, японский, корейский)
            // подтверждает выбор иероглифа, а не отправляет: иначе уходит
            // недописанное слово. 229 — так тот же момент помечает Safari.
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === "Enter" && !e.shiftKey && onEnter) {
              e.preventDefault();
              onEnter();
            }
          }}
          className={cn(
            "max-h-40 w-full overflow-y-auto whitespace-pre-wrap break-words outline-none",
            "[&_img]:mx-[1px]",
            className,
          )}
        />
        {isEmpty && placeholder && (
          <span className="pointer-events-none absolute left-0 top-0 select-none text-muted-foreground">
            {placeholder}
          </span>
        )}
      </div>
    );
  },
);
